import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { pharmacyApi, type BatchOption, PrescriptionQueueItem } from "@/api/pharmacy";
import { format } from "date-fns";
import { Loader2, Pill } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function PharmacyQueuePage() {
  const [queueType, setQueueType] = useState<"OPD" | "IPD">("OPD");
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRow, setConfirmRow] = useState<PrescriptionQueueItem | null>(null);

  const { data: queue, isLoading } = useQuery({
    queryKey: ["pharmacy-queue", queueType],
    queryFn: async () => {
      const res = await pharmacyApi.getPrescriptionQueue({
        type: queueType,
      });
      return res.data?.data ?? [];
    },
  });

  const { data: fifoByDrugId, isLoading: loadingFifo } = useQuery({
    queryKey: ["pharmacy-issue-fifo", confirmRow?.id],
    enabled: confirmOpen && !!confirmRow,
    queryFn: async () => {
      const row = confirmRow!;
      const uniqueDrugIds = Array.from(new Set((row.items ?? []).map((it) => it.drugId).filter(Boolean)));
      const pairs = await Promise.all(
        uniqueDrugIds.map(async (drugId) => {
          const [batchRes, stockRes] = await Promise.all([
            pharmacyApi.getBatchesForDrug(drugId, 1),
            pharmacyApi.getStock(drugId),
          ]);
          const list = (batchRes.data?.data ?? []) as BatchOption[];
          const fifo = list[0] ?? null;
          const totalStock = Number(stockRes.data?.data?.quantity ?? 0);
          return [drugId, { fifo, totalStock }] as const;
        })
      );
      return Object.fromEntries(pairs) as Record<string, { fifo: BatchOption | null; totalStock: number }>;
    },
  });

  const issueOPDMutation = useMutation({
    mutationFn: (payload: { prescriptionId: string; patientId: string; appointmentId?: string }) =>
      pharmacyApi.issueOPD(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pharmacy-queue", "OPD"] });
      toast.success("Medicines dispensed and bill created.");
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err.response?.data?.message ?? "OPD issue failed");
    },
  });

  const issueIPDMutation = useMutation({
    mutationFn: (prescriptionId: string) => pharmacyApi.issueIPD({ prescriptionId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pharmacy-queue", "IPD"] });
      toast.success("IPD medicines issued (accrued to admission).");
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err.response?.data?.message ?? "IPD issue failed");
    },
  });

  const handleIssueOPD = (row: PrescriptionQueueItem) => {
    if (!row.patientId) return;
    setConfirmRow(row);
    setConfirmOpen(true);
  };

  const handleIssueIPD = (row: PrescriptionQueueItem) => {
    issueIPDMutation.mutate(row.id);
  };

  const list = (queue ?? []) as PrescriptionQueueItem[];
  const fifoReady = !!confirmRow && !!fifoByDrugId && !loadingFifo;
  const fifoMissingCount = useMemo(() => {
    if (!confirmRow || !fifoByDrugId) return 0;
    const items = confirmRow.items ?? [];
    return items.filter((it) => {
      const row = fifoByDrugId[it.drugId];
      return !row?.fifo || (row.totalStock ?? 0) <= 0;
    })?.length ?? 0;
  }, [confirmRow, fifoByDrugId]);

  const insufficientCount = useMemo(() => {
    if (!confirmRow || !fifoByDrugId) return 0;
    return (confirmRow.items ?? []).filter((it) => {
      const row = fifoByDrugId[it.drugId];
      const avail = row?.totalStock ?? 0;
      return avail > 0 && avail < Number(it.quantity ?? 0);
    }).length;
  }, [confirmRow, fifoByDrugId]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pill className="h-5 w-5" />
            Prescription Queue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={queueType} onValueChange={(v) => setQueueType(v as "OPD" | "IPD")}>
            <TabsList>
              <TabsTrigger value="OPD">OPD</TabsTrigger>
              <TabsTrigger value="IPD">IPD</TabsTrigger>
            </TabsList>
            <TabsContent value="OPD" className="mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Prescription #</TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead>Doctor</TableHead>
                      <TableHead>Pending items</TableHead>
                      <TableHead>Valid until</TableHead>
                      <TableHead className="w-[100px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          No pending OPD prescriptions
                        </TableCell>
                      </TableRow>
                    ) : (
                      list.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="font-mono">{row.prescriptionNumber}</TableCell>
                          <TableCell>
                            {row.patient?.name ?? "-"} {row.patient?.uhid && `(${row.patient.uhid})`}
                          </TableCell>
                          <TableCell>{row.doctor?.name ?? "-"}</TableCell>
                          <TableCell>{row.items?.length ?? 0}</TableCell>
                          <TableCell>{row.validUntil ? format(new Date(row.validUntil), "dd MMM yyyy") : "-"}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => handleIssueOPD(row)}
                              disabled={issueOPDMutation.isPending}
                            >
                              {issueOPDMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Issue"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
            <TabsContent value="IPD" className="mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Prescription #</TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead>Admission / Ward</TableHead>
                      <TableHead>Pending items</TableHead>
                      <TableHead>Valid until</TableHead>
                      <TableHead className="w-[100px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          No pending IPD prescriptions
                        </TableCell>
                      </TableRow>
                    ) : (
                      list.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="font-mono">{row.prescriptionNumber}</TableCell>
                          <TableCell>
                            {row.patient?.name ?? "-"} {row.patient?.uhid && `(${row.patient.uhid})`}
                          </TableCell>
                          <TableCell>
                            {row.admission?.admissionNumber ?? "-"}
                            {row.admission?.bed?.ward?.name && ` / ${row.admission.bed.ward.name}`}
                          </TableCell>
                          <TableCell>{row.items?.length ?? 0}</TableCell>
                          <TableCell>{row.validUntil ? format(new Date(row.validUntil), "dd MMM yyyy") : "-"}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => handleIssueIPD(row)}
                              disabled={issueIPDMutation.isPending}
                            >
                              {issueIPDMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Issue"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog
        open={confirmOpen}
        onOpenChange={(v) => {
          setConfirmOpen(v);
          if (!v) setConfirmRow(null);
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Confirm batches before issuing</DialogTitle>
          </DialogHeader>

          {!confirmRow ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : loadingFifo ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Fetching FIFO batches…
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="font-medium">Prescription:</span>{" "}
                <span className="font-mono">{confirmRow.prescriptionNumber}</span>{" "}
                <span className="text-muted-foreground">
                  · {confirmRow.patient?.name ?? "-"}
                </span>
              </div>

              {insufficientCount > 0 && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
                  {insufficientCount} medicine(s) need more stock than available. Adjust prescription qty or stock-in first.
                </div>
              )}

              {fifoMissingCount > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {fifoMissingCount} medicine(s) have no available batch in stock (or could not be fetched). Please check stock & batches.
                </div>
              )}

              <div className="overflow-auto rounded-md border">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="p-2 text-left">Medicine</th>
                      <th className="p-2 text-right">Qty</th>
                      <th className="p-2 text-left">FIFO batch</th>
                      <th className="p-2 text-left">Expiry</th>
                      <th className="p-2 text-right">Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(confirmRow.items ?? []).map((it) => {
                      const stockRow = fifoByDrugId?.[it.drugId] ?? null;
                      const fifo = stockRow?.fifo ?? null;
                      const avail = stockRow?.totalStock ?? 0;
                      const insufficient = avail > 0 && avail < Number(it.quantity ?? 0);
                      const noStock = avail <= 0;
                      return (
                        <tr key={it.id} className="border-t">
                          <td className="p-2">
                            <div className="font-medium">{it.drug?.name ?? it.drugId}</div>
                            {(it.drug?.strength || it.drug?.form || it.drug?.unit) && (
                              <div className="text-xs text-muted-foreground">
                                {[it.drug?.strength, it.drug?.form, it.drug?.unit].filter(Boolean).join(" ")}
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-right">{it.quantity}</td>
                          <td className="p-2">{fifo?.batchNo ?? <span className="text-muted-foreground">—</span>}</td>
                          <td className="p-2">
                            {fifo?.expiryDate ? String(fifo.expiryDate).slice(0, 10) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className={`p-2 text-right ${insufficient || noStock ? "text-rose-600 font-semibold" : ""}`}>
                            {stockRow ? avail : <span className="text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmOpen(false);
                setConfirmRow(null);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={
                issueOPDMutation.isPending ||
                !confirmRow?.patientId ||
                !fifoReady ||
                fifoMissingCount > 0 ||
                insufficientCount > 0
              }
              onClick={() => {
                if (!confirmRow?.patientId) return;
                issueOPDMutation.mutate({
                  prescriptionId: confirmRow.id,
                  patientId: confirmRow.patientId,
                });
                setConfirmOpen(false);
                setConfirmRow(null);
              }}
            >
              {issueOPDMutation.isPending ? "Issuing…" : "Confirm & Issue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
